import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { GridApiPro } from '@mui/x-data-grid-pro/models'
import { GridColDef } from '@mui/x-data-grid/models'
import { NoteListItem } from '@finance-lab/utils/types'
import { NoteEntityRef, NotesApi } from '../../api/NotesApi'
import { formatDateYYYYMMDDhhmm } from '../../packages/utils/numberFormatters'
import { useNoteEncryption } from '../../hooks/useNoteEncryption'
import useAuth from '../../hooks/useAuth'
import useConfirmationDialog from '../../components/dialogs/ConfirmationDialog'
import { NoteEditorDialog } from '../components/NoteEditorDialog'
import { NoteEntityLink } from './NoteEntityLink'
import useTheme from '@mui/system/useTheme'
import { gridClasses } from '@mui/x-data-grid/constants'

const notesApi = new NotesApi()
const NOTE_PREVIEW_LENGTH = 200

type NoteRow = NoteListItem & {
    decryptedContent: string
}

const NoteContentCell = ({ content, expandAll }: { content: string; expandAll: boolean }) => {
    const [expanded, setExpanded] = useState(false)
    const isTruncated = content.length > NOTE_PREVIEW_LENGTH
    const showFull = expandAll || expanded || !isTruncated
    const displayContent = showFull ? content : `${content.slice(0, NOTE_PREVIEW_LENGTH).trimEnd()}…`

    return (
        <Box sx={{ py: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {displayContent}
            {isTruncated && !expandAll ? (
                <Button
                    onClick={(event) => {
                        event.stopPropagation()
                        setExpanded((value) => !value)
                    }}
                    size='small'
                    sx={{ display: 'block', mt: 0.5, px: 0 }}
                >
                    {expanded ? 'Visa mindre' : 'Visa mer'}
                </Button>
            ) : null}
        </Box>
    )
}

const toEntityRef = (item: NoteListItem): NoteEntityRef => {
    if (item.entityType === 'Issue') {
        return { issueId: item.entityId }
    }
    if (item.entityType === 'Warrant') {
        return { warrantId: item.entityId }
    }
    return { convertibleId: item.entityId }
}

export const NotesView = () => {
    const { user } = useAuth()
    const { confirm, ConfirmationDialog } = useConfirmationDialog()
    const theme = useTheme()
    const { decrypt, error: cryptoError, isLoading: isCryptoLoading, isReady } = useNoteEncryption()
    const [notes, setNotes] = useState<NoteRow[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [editingEntityRef, setEditingEntityRef] = useState<NoteEntityRef | null>(null)
    const [expandAllNotes, setExpandAllNotes] = useState(false)
    const apiRef = useRef<GridApiPro | null>(null)

    const hasTruncatedNotes = notes.some((note) => note.decryptedContent.length > NOTE_PREVIEW_LENGTH)
    const loadNotes = useCallback(async () => {
        if (!isReady) {
            return
        }

        setIsLoading(true)
        setLoadError(null)

        try {
            const result = await notesApi.listNotes()
            const rows = await Promise.all(
                result.map(async (note) => {
                    let decryptedContent = ''

                    if (note.content) {
                        try {
                            decryptedContent = await decrypt(note.content)
                        } catch (err) {
                            console.error(err)
                            decryptedContent = 'Kunde inte läsa anteckningen.'
                        }
                    }

                    return {
                        ...note,
                        decryptedContent,
                    }
                }),
            )

            setNotes(rows)
        } catch (err) {
            console.error(err)
            setLoadError('Kunde inte hämta anteckningarna.')
            setNotes([])
        } finally {
            setIsLoading(false)
        }
    }, [decrypt, isReady])

    const handleDelete = useCallback(
        async (note: NoteRow) => {
            const confirmed = await confirm('Är du säker på att du vill ta bort anteckningen?', {
                title: 'Ta bort anteckning',
                confirmLabel: 'Ta bort',
            })
            if (!confirmed) {
                return
            }

            try {
                await notesApi.deleteNote(toEntityRef(note))
                await loadNotes()
            } catch (err) {
                console.error(err)
                setLoadError('Kunde inte ta bort anteckningen.')
            }
        },
        [confirm, loadNotes],
    )

    useEffect(() => {
        if (!isReady) {
            return
        }

        void loadNotes()
    }, [isReady, loadNotes])

    useEffect(() => {
        apiRef.current?.resetRowHeights()
    }, [expandAllNotes, notes])

    const columns = useMemo<GridColDef<NoteRow>[]>(
        () => [
            {
                field: 'name',
                headerName: 'Namn',
                flex: 1,
                minWidth: 240,
                sortable: false,
                renderCell: ({ row }) => <NoteEntityLink item={row} />,
            },
            {
                field: 'updatedAt',
                headerName: 'Uppdaterad',
                width: 134,
                valueFormatter: (value) => formatDateYYYYMMDDhhmm(value),
            },
            {
                field: 'decryptedContent',
                headerName: 'Anteckning',
                flex: 2,
                minWidth: 280,
                sortable: false,
                renderCell: ({ value }) => <NoteContentCell content={value ?? ''} expandAll={expandAllNotes} />,
            },
            {
                field: 'actions',
                headerName: '',
                width: 74,
                sortable: false,
                renderCell: ({ row }) => (
                    <Stack direction='row' spacing={0.5}>
                        <Tooltip title='Redigera'>
                            <span>
                                <IconButton
                                    aria-label='Redigera'
                                    disabled={!!cryptoError || isCryptoLoading}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        setEditingEntityRef(toEntityRef(row))
                                    }}
                                    size='small'
                                >
                                    <EditIcon color='warning' />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title='Ta bort'>
                            <span>
                                <IconButton
                                    aria-label='Ta bort'
                                    disabled={!!cryptoError || isCryptoLoading}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        void handleDelete(row)
                                    }}
                                    size='small'
                                >
                                    <DeleteIcon color='error' />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        [cryptoError, expandAllNotes, handleDelete, isCryptoLoading],
    )

    if (!user?.groups?.includes('superadmin')) {
        return null
    }

    const disableSelectedSellOutlineStyle = {
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
        },
    }

    return (
        <>
            <ConfirmationDialog />
            <Helmet title='Anteckningar' />
            <Typography variant='h3' gutterBottom>
                Anteckningar
            </Typography>
            <Stack
                direction='row'
                sx={{ alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between', mb: 4 }}
            >
                <Typography color='text.secondary'>
                    Dina anteckningar på emissioner, teckningsoptioner och konvertibler.
                </Typography>
                {hasTruncatedNotes ? (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={expandAllNotes}
                                onChange={(event) => setExpandAllNotes(event.target.checked)}
                                disableFocusRipple
                                disableRipple
                                size='small'
                                sx={{ marginLeft: 0, paddingLeft: 0 }}
                            />
                        }
                        label='Visa hela anteckningarna'
                        sx={{ m: 0 }}
                    />
                ) : null}
            </Stack>

            {cryptoError ? (
                <Alert severity='error' sx={{ mb: 2 }}>
                    {cryptoError}
                </Alert>
            ) : null}
            {loadError ? (
                <Alert severity='warning' sx={{ mb: 2 }}>
                    {loadError}
                </Alert>
            ) : null}

            <DataGridPro
                apiRef={apiRef}
                sx={{
                    ...disableSelectedSellOutlineStyle,
                    width: '100%',
                    '& .even': {
                        backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                    },
                    '& .odd': {
                        // backgroundColor: theme.palette.rowColor.light, // Use palette color for odd rows
                    },
                    '& .MuiDataGrid-cell': {
                        paddingY: 4,
                        alignItems: 'center',
                        display: 'flex',
                    },
                    '& .MuiDataGrid-cell[data-field="actions"], & .MuiDataGrid-columnHeader[data-field="actions"]': {
                        px: 0,
                    },
                }}
                autoHeight
                columns={columns}
                density='compact'
                disableColumnMenu
                disableRowSelectionOnClick
                getRowHeight={() => 'auto'}
                loading={isLoading || isCryptoLoading}
                pageSizeOptions={[25, 50, 100]}
                pagination
                initialState={{
                    pagination: { paginationModel: { pageSize: 25 } },
                }}
                rows={notes}
            />

            {editingEntityRef ? (
                <NoteEditorDialog
                    entityRef={editingEntityRef}
                    onClose={() => setEditingEntityRef(null)}
                    onDeleted={() => void loadNotes()}
                    onSaved={() => void loadNotes()}
                    open
                />
            ) : null}
        </>
    )
}
